import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useFormContext } from 'react-hook-form'
import { InputAdornment } from '@mui/material'

import { theme } from '../../themes/theme'
import { config } from '../../config'
import { preventDecimalInput } from '../../util/inputHandlers'
import { BILL_TYPE, billTypeMap, getBillTypeOptions, getApplicationFeeLineItems } from '../../util/billingConsts'
import { formatPriceStr } from '../../util/billFormatting'
import { calculateBillTotal } from '../../util/strings'
import { CenteredFlexColumn, Divider, FlexColumn, FlexRow, MedBGContainer, SpaceBetweenRowContainer } from '../styles/Containers'
import { TotalsContainer, TotalDue, TotalAmount, TableContainer } from '../styles/BillStyles'
import { SelectMenuItem } from '../styles/TextField'
import BodyText, { BoldBodyText, BoldFormTitle, ErrorText } from '../styles/Text'
import FormInput from '../inputFields/FormInput'
import FormSelect from '../inputFields/FormSelect'
import BillLineItemsTableHeader from './BillLineItemsTableHeader'
import BillLineItems from './BillLineItems'

const MIN_PERCENT_DUE = 0
const MAX_PERCENT_DUE = 100

const BillItemsContainer = styled(MedBGContainer)`
  border: ${({ error }) => (error ? `1px solid ${theme.colors.error}` : `1px solid ${theme.colors.background.white}`)};
  background: ${({ disabled }) => (disabled ? theme.colors.background.light : theme.colors.background.medium)};
`

const StartAlignedRow = styled(SpaceBetweenRowContainer)`
  align-items: start;
`

const CenteredRow = styled(FlexRow)`
  align-items: center;
`

const TotalWrapper = styled(CenteredRow)`
  height: 38px;
`

function BillForm({ disabled = false, onlyApplicationFeeAllowed = false }) {
  const [subtotal, setSubtotal] = useState(0)
  const [totalDue, setTotalDue] = useState(0)
  const [defaultType, setDefaultType] = useState()
  const [updatePercent, setUpdatePercent] = useState(false)

  const { watch, setValue, getValues, formState } = useFormContext()

  const type = watch('type')
  const lineItems = watch('lineItems')
  const percentDue = watch('percentDue')

  const billTypeOptions = useMemo(
    () =>
      getBillTypeOptions({
        type: onlyApplicationFeeAllowed ? 'onlyApplicationFee' : config.foi.APPLICATION_FEE ? 'withApplicationFee' : 'withoutApplicationFee'
      }),
    [config.foi.APPLICATION_FEE, onlyApplicationFeeAllowed]
  )
  const isApplicationFeeBill = type === BILL_TYPE.APPLICATION_FEE && config.foi.APPLICATION_FEE
  const formFieldsDisabled = disabled || isApplicationFeeBill

  function onEditLineItem() {
    const newSubtotal = calculateBillTotal({ lineItems })
    setSubtotal(newSubtotal)
  }

  useEffect(() => {
    setDefaultType(getValues('type') || (onlyApplicationFeeAllowed ? BILL_TYPE.APPLICATION_FEE : BILL_TYPE.FEE_ESTIMATE))
    if (!type) {
      setUpdatePercent(true)
      setValue('type', onlyApplicationFeeAllowed ? BILL_TYPE.APPLICATION_FEE : BILL_TYPE.FEE_ESTIMATE)
    }
  }, [])

  useEffect(() => {
    if (type === BILL_TYPE.APPLICATION_FEE && config.foi.APPLICATION_FEE) {
      const amt = config.foi.applicationFeeAmount
      setValue('percentDue', config.foi.bill.percentDue.APPLICATION_FEE)
      setValue('lineItems', getApplicationFeeLineItems(amt))
      return
    }

    if (updatePercent) setValue('percentDue', config.foi.bill.percentDue[type])

    if (defaultType && type !== defaultType) {
      setUpdatePercent(true)
      setValue('percentDue', config.foi.bill.percentDue[type])
    }
  }, [type])

  useEffect(() => {
    const newSubtotal = calculateBillTotal({ lineItems })
    setSubtotal(newSubtotal)
  }, [lineItems])

  useEffect(() => {
    const newTotalDue = (subtotal * percentDue) / 100
    setTotalDue(newTotalDue)
  }, [subtotal, percentDue])

  useEffect(() => {
    if (percentDue === '' || percentDue === undefined || percentDue === null) return
    if (percentDue < MIN_PERCENT_DUE) setValue('percentDue', MIN_PERCENT_DUE)
    else if (percentDue > MAX_PERCENT_DUE) setValue('percentDue', MAX_PERCENT_DUE)
  }, [percentDue, setValue])

  const lineItemError = !!formState.errors.lineItems?.message
  const rootError = !!formState.errors.lineItems?.root
  const helperText = rootError ? formState.errors.lineItems?.root?.message : formState.errors?.lineItems?.message

  return (
    <FlexColumn gap="15px">
      <SpaceBetweenRowContainer>
        <BoldFormTitle>{billTypeMap[type]}</BoldFormTitle>
        <CenteredRow gap="15px">
          <BodyText id="type-label">Type</BodyText>
          {defaultType !== undefined && (
            <FormSelect
              id="type"
              options={billTypeOptions}
              defaultValue={defaultType}
              minwidth="175px"
              disabled={disabled}
              props={{ 'aria-labelledby': 'type-label' }}>
              {billTypeOptions.map(b => (
                <SelectMenuItem key={b.value} value={b.value}>
                  {b.label}
                </SelectMenuItem>
              ))}
            </FormSelect>
          )}
        </CenteredRow>
      </SpaceBetweenRowContainer>
      <BillItemsContainer padding="30px" error={lineItemError || rootError} disabled={formFieldsDisabled}>
        <TableContainer>
          <BillLineItemsTableHeader editable />
          <Divider />
          <FlexColumn gap="8px" width="100%">
            <BillLineItems disabled={formFieldsDisabled} onEditLineItem={onEditLineItem} />
            {(lineItemError || rootError) && (
              <CenteredFlexColumn>
                <ErrorText>{helperText}</ErrorText>
              </CenteredFlexColumn>
            )}
          </FlexColumn>
        </TableContainer>
      </BillItemsContainer>
      <Divider />
      <TotalsContainer>
        <SpaceBetweenRowContainer>
          <BoldBodyText>Total</BoldBodyText>
          <TotalAmount>{formatPriceStr(subtotal)}</TotalAmount>
        </SpaceBetweenRowContainer>
        <StartAlignedRow>
          <FlexRow>
            <TotalWrapper>
              <TotalDue>Total {totalDue < 0 ? 'refunded' : 'due'}</TotalDue>
            </TotalWrapper>
            <FormInput
              id="percentDue"
              minwidth="100px"
              disabled={formFieldsDisabled}
              props={{
                type: 'number',
                inputProps: {
                  'aria-label': 'Percent Due',
                  onKeyPress: preventDecimalInput,
                  min: MIN_PERCENT_DUE,
                  max: MAX_PERCENT_DUE
                }
              }}
              inputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </FlexRow>
          <TotalWrapper>{formatPriceStr(totalDue)}</TotalWrapper>
        </StartAlignedRow>
      </TotalsContainer>
    </FlexColumn>
  )
}

BillForm.propTypes = {
  disabled: PropTypes.bool,
  onlyApplicationFeeAllowed: PropTypes.bool
}

export default BillForm
